import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { useWODocumentSettings, useUpsertWODocumentSettings, DEFAULT_TOGGLES, DEFAULT_SECTION_ORDER } from "@/hooks/useWODocumentSettings";
import { toast } from "sonner";
import { Download, Save, Maximize2, Minimize2, GripVertical } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import commandxLogo from "@/assets/commandx-logo.png";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtRate = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);

const SECTION_LABELS: Record<string, string> = {
  scope: "Scope of Work",
  site_info: "Site Information",
  terms: "Commercial Terms",
  contractor_scope: "Contractor Scope",
  signatures: "Signatures",
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  workOrder: any;
  woLines: any[];
  project: any;
}

const TOGGLE_SECTIONS = [
  {
    title: "Project Info",
    keys: [
      { key: "show_project_name", label: "Project name & number" },
      { key: "show_site_address", label: "Site address" },
      { key: "show_site_contact", label: "Site contact" },
      { key: "show_mobilization_date", label: "Mobilization date" },
      { key: "show_completion_date", label: "Completion date" },
      { key: "show_other_subs", label: "Other subs on site" },
    ],
  },
  {
    title: "Scope",
    keys: [
      { key: "show_line_items", label: "Line items + qty + rate" },
      { key: "show_product_specs", label: "Product specifications" },
      { key: "show_special_instructions", label: "Special instructions" },
      { key: "show_sov_reference", label: "SOV reference numbers" },
      { key: "show_internal_codes", label: "Internal codes" },
    ],
  },
  {
    title: "Commercial Terms",
    keys: [
      { key: "show_wo_total", label: "Total WO value" },
      { key: "show_payment_terms", label: "Payment terms" },
      { key: "show_retainage", label: "Retainage" },
      { key: "show_lien_waiver_req", label: "Lien waiver required" },
      { key: "show_billing_instructions", label: "Billing instructions" },
    ],
  },
  {
    title: "Site Requirements",
    keys: [
      { key: "show_work_hours", label: "Work hours" },
      { key: "show_parking", label: "Parking instructions" },
      { key: "show_badge_requirements", label: "Badge requirements" },
      { key: "show_safety_requirements", label: "Safety requirements" },
    ],
  },
  {
    title: "Signatures",
    keys: [
      { key: "show_signature_block", label: "Signature block" },
      { key: "show_company_signature", label: "Company signature line" },
      { key: "show_sub_signature", label: "Sub signature line" },
      { key: "show_contractor_scope", label: "Contractor scope of work" },
    ],
  },
];

/* ── Sortable section chip ── */
const SortableSectionChip = ({ id }: { id: string }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm cursor-grab select-none",
        isDragging && "opacity-50 shadow-lg ring-2 ring-primary"
      )}
    >
      <GripVertical className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span>{SECTION_LABELS[id] ?? id}</span>
    </div>
  );
};

export const GenerateWODialog = ({ open, onOpenChange, workOrder, woLines, project }: Props) => {
  const { data: savedSettings } = useWODocumentSettings();
  const { data: companyProfile } = useCompanyProfile();
  const upsertSettings = useUpsertWODocumentSettings();
  const [toggles, setToggles] = useState<Record<string, boolean>>(DEFAULT_TOGGLES);
  const [expanded, setExpanded] = useState(false);
  const [sectionOrder, setSectionOrder] = useState<string[]>(DEFAULT_SECTION_ORDER);
  const [scopeText, setScopeText] = useState("");
  const previewRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (savedSettings) {
      const loaded: Record<string, boolean> = {};
      Object.keys(DEFAULT_TOGGLES).forEach((k) => {
        loaded[k] = (savedSettings as any)[k] ?? DEFAULT_TOGGLES[k];
      });
      setToggles(loaded);
      setScopeText(savedSettings.contractor_scope_text ?? "");
      if (savedSettings.section_order && Array.isArray(savedSettings.section_order)) {
        setSectionOrder(savedSettings.section_order);
      }
    }
  }, [savedSettings]);

  const toggle = (key: string) => setToggles((p) => ({ ...p, [key]: !p[key] }));

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSectionOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const handleSaveDefaults = async () => {
    try {
      await upsertSettings.mutateAsync({
        ...toggles,
        section_order: sectionOrder,
        contractor_scope_text: scopeText || null,
      } as any);
      toast.success("Defaults saved");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleDownload = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow || !previewRef.current) return;
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Work Order ${workOrder.number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; padding: 40px; font-size: 13px; line-height: 1.5; }
        .header { border-bottom: 3px solid #1a1a1a; padding-bottom: 12px; margin-bottom: 20px; }
        .header h1 { font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: #555; margin-bottom: 2px; }
        .header h2 { font-size: 20px; font-weight: 700; }
        .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
        .meta-box { background: #f8f8f8; border: 1px solid #e0e0e0; border-radius: 4px; padding: 12px; }
        .meta-box h3 { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-bottom: 6px; }
        .meta-box p { margin: 2px 0; }
        .scope-table { width: 100%; border-collapse: collapse; margin: 16px 0; }
        .scope-table th { background: #1a1a1a; color: white; text-align: left; padding: 8px 10px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
        .scope-table td { padding: 8px 10px; border-bottom: 1px solid #e0e0e0; }
        .scope-table tr:nth-child(even) td { background: #fafafa; }
        .scope-table .text-right { text-align: right; }
        .total-row { font-weight: 700; background: #f0f0f0 !important; }
        .total-row td { border-top: 2px solid #1a1a1a; }
        .section { margin: 24px 0; }
        .section h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 10px; }
        .terms-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .terms-grid p { margin: 0; }
        .sig-block { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
        .sig-line { border-top: 1px solid #1a1a1a; padding-top: 4px; margin-top: 28px; font-size: 11px; color: #888; }
        .footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #ccc; font-size: 10px; color: #999; text-align: center; }
        .spec-note { font-size: 12px; color: #555; padding-left: 16px; font-style: italic; }
        .contractor-scope p { margin-bottom: 6px; }
        @media print { body { padding: 20px; } }
      </style></head><body>${previewRef.current.innerHTML}</body></html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
  };

  const wo = workOrder;
  const p = project;
  const rawLines = woLines.filter((l: any) => l.work_order_id === wo.id && l.status !== "voided" && l.status !== "released");

  // Render every sub-line individually. For multi-child SOV groups, prepend a
  // parent header row so Labor / Material breakdown is visible on the document.
  const lines = useMemo(() => {
    const groups: Record<string, any[]> = {};
    const order: string[] = [];
    rawLines.forEach((wol: any) => {
      const key = wol.sov_line_id ?? wol.id;
      if (!groups[key]) {
        groups[key] = [];
        order.push(key);
      }
      groups[key].push(wol);
    });

    const out: any[] = [];
    order.forEach((key) => {
      const children = groups[key];
      const unitNums = children
        .map((c: any) => c.unit_schedule?.unit_number)
        .filter(Boolean)
        .sort();
      const unitLabel = unitNums.length > 0
        ? `Unit${unitNums.length > 1 ? "s" : ""} ${unitNums.length > 6 ? unitNums.slice(0, 5).join(", ") + ` +${unitNums.length - 5} more` : unitNums.join(", ")}`
        : "";

      if (children.length === 1) {
        const wol = children[0];
        out.push({
          id: wol.id,
          isHeader: false,
          description: wol.description,
          unitLabel,
          quantity: Number(wol.quantity),
          contract_rate: Number(wol.contract_rate),
          unit: wol.unit,
          product_spec: wol.product_spec,
          special_instructions: wol.special_instructions,
        });
        return;
      }

      // Multi-child group: header + one row per sub-line
      const first = children[0];
      const groupTotal = children.reduce(
        (s: number, c: any) => s + Number(c.quantity) * Number(c.contract_rate),
        0,
      );
      out.push({
        id: `header-${key}`,
        isHeader: true,
        description: first.description,
        unitLabel,
        groupTotal,
      });
      children.forEach((c: any) => {
        out.push({
          id: c.id,
          isHeader: false,
          isChild: true,
          description: c.description,
          unitLabel: "",
          quantity: Number(c.quantity),
          contract_rate: Number(c.contract_rate),
          unit: c.unit,
          product_spec: c.product_spec,
          special_instructions: c.special_instructions,
        });
      });
    });
    return out;
  }, [rawLines]);

  const woTotal = lines.reduce((s: number, l: any) => l.isHeader ? s : s + Number(l.quantity) * Number(l.contract_rate), 0);
  const contractor = wo.vendors;
  const today = format(new Date(), "MMMM d, yyyy");

  const fmtTime = (t: string | null) => {
    if (!t) return "";
    const [h, m] = t.split(":");
    const hr = parseInt(h);
    return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
  };
  const paymentLabel = (t: string | null) => {
    const map: Record<string, string> = { net_30: "Net 30", net_15: "Net 15", net_45: "Net 45", net_60: "Net 60", due_on_receipt: "Due on Receipt" };
    return map[t ?? ""] ?? t ?? "Net 30";
  };

  /* ── Section renderers ── */
  const renderScope = () => {
    if (!toggles.show_line_items || lines.length === 0) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, textTransform: "uppercase" as const, letterSpacing: 1, borderBottom: "1px solid hsl(var(--border))", paddingBottom: 4, marginBottom: 10 }}>Scope of Work</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "hsl(var(--foreground))", color: "hsl(var(--background))" }}>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11 }}>#</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11 }}>Description</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11 }}>Qty</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 11 }}>Unit</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11 }}>Rate</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 11 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let displayNum = 0;
              let subLetter = 0;
              let lastHeaderNum = 0;
              return lines.map((l: any, i: number) => {
                let label = "";
                if (l.isHeader) {
                  displayNum += 1;
                  lastHeaderNum = displayNum;
                  subLetter = 0;
                  label = String(displayNum);
                } else if (l.isChild) {
                  subLetter += 1;
                  label = `${lastHeaderNum}${String.fromCharCode(96 + subLetter)}`;
                } else {
                  displayNum += 1;
                  label = String(displayNum);
                }

                if (l.isHeader) {
                  return (
                    <tr key={l.id} style={{ background: "hsl(var(--muted) / 0.6)" }}>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", fontWeight: 600 }}>{label}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", fontWeight: 600 }}>
                        <div>{l.description}</div>
                        {l.unitLabel && (
                          <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", marginTop: 2, fontWeight: 400 }}>{l.unitLabel}</div>
                        )}
                      </td>
                      <td colSpan={3} style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", textAlign: "right", fontSize: 10, color: "hsl(var(--muted-foreground))" }}>Subtotal</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", textAlign: "right", fontWeight: 600 }}>{fmt(Number(l.groupTotal))}</td>
                    </tr>
                  );
                }

                return (
                  <tr key={l.id} style={{ background: i % 2 === 0 ? "transparent" : "hsl(var(--muted) / 0.5)" }}>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))" }}>{label}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", paddingLeft: l.isChild ? 24 : 10 }}>
                      <div>{l.description}</div>
                      {l.unitLabel && (
                        <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>{l.unitLabel}</div>
                      )}
                      {toggles.show_product_specs && l.product_spec && (
                        <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", fontStyle: "italic", paddingLeft: 8, marginTop: 2 }}>Spec: {l.product_spec}</div>
                      )}
                      {toggles.show_special_instructions && l.special_instructions && (
                        <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", fontStyle: "italic", paddingLeft: 8, marginTop: 2 }}>Note: {l.special_instructions}</div>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", textAlign: "right" }}>{Number(l.quantity).toLocaleString()}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))" }}>{l.unit}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", textAlign: "right" }}>{fmtRate(Number(l.contract_rate))}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid hsl(var(--border))", textAlign: "right" }}>{fmt(Number(l.quantity) * Number(l.contract_rate))}</td>
                  </tr>
                );
              });
            })()}
            {toggles.show_wo_total && (
              <tr style={{ fontWeight: 700, background: "hsl(var(--muted))" }}>
                <td colSpan={5} style={{ padding: "8px 10px", borderTop: "2px solid hsl(var(--foreground))", textAlign: "right" }}>Total Work Order Value:</td>
                <td style={{ padding: "8px 10px", borderTop: "2px solid hsl(var(--foreground))", textAlign: "right" }}>{fmt(woTotal)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSiteInfo = () => {
    if (!(toggles.show_site_contact || toggles.show_mobilization_date || toggles.show_completion_date || toggles.show_work_hours || toggles.show_parking || toggles.show_badge_requirements || toggles.show_safety_requirements)) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, textTransform: "uppercase" as const, letterSpacing: 1, borderBottom: "1px solid hsl(var(--border))", paddingBottom: 4, marginBottom: 10 }}>Site Information</h3>
        {toggles.show_site_contact && p.site_contact_name && <p>Site Contact: {p.site_contact_name} — {p.site_contact_phone}</p>}
        {toggles.show_mobilization_date && p.mobilization_date && <p>Mobilization: {format(new Date(p.mobilization_date + "T00:00:00"), "MMMM d, yyyy")}</p>}
        {toggles.show_completion_date && p.scheduled_completion && <p>Completion: {format(new Date(p.scheduled_completion + "T00:00:00"), "MMMM d, yyyy")}</p>}
        {toggles.show_work_hours && <p>Work Hours: {p.work_days ?? "Mon-Fri"} {fmtTime(p.work_hours_start)} – {fmtTime(p.work_hours_end)}</p>}
        {toggles.show_parking && p.parking_instructions && <p>Parking: {p.parking_instructions}</p>}
        {toggles.show_badge_requirements && p.badge_required && <p>Badge: Required {p.badge_instructions ? `— ${p.badge_instructions}` : ""}</p>}
        {toggles.show_safety_requirements && p.safety_requirements && <p>Safety: {p.safety_requirements}</p>}
      </div>
    );
  };

  const renderTerms = () => {
    if (!(toggles.show_payment_terms || toggles.show_retainage || toggles.show_lien_waiver_req || toggles.show_billing_instructions)) return null;
    return (
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 12, textTransform: "uppercase" as const, letterSpacing: 1, borderBottom: "1px solid hsl(var(--border))", paddingBottom: 4, marginBottom: 10 }}>Commercial Terms</h3>
        {toggles.show_payment_terms && <p>Payment Terms: {paymentLabel(p.default_payment_terms)} from invoice approval</p>}
        {toggles.show_retainage && <p>Retainage: {p.default_retainage_pct ?? 10}% held until final completion and lien waiver received</p>}
        {toggles.show_billing_instructions && <p>Billing: Reference {wo.number} on all invoices</p>}
        {toggles.show_lien_waiver_req && <p>Lien Waiver: Conditional lien waiver required with each progress billing. Unconditional required for final.</p>}
        <p style={{ marginTop: 8 }}>Change Orders: No additional work without written CO approval from {companyProfile?.name ?? "Company"} PM. Verbal authorizations not binding.</p>
      </div>
    );
  };

  const renderContractorScope = () => {
    if (!toggles.show_contractor_scope || !scopeText) return null;
    return (
      <div style={{ marginBottom: 24 }} className="contractor-scope">
        <h3 style={{ fontSize: 12, textTransform: "uppercase" as const, letterSpacing: 1, borderBottom: "1px solid hsl(var(--border))", paddingBottom: 4, marginBottom: 10 }}>Contractor Scope of Work</h3>
        {scopeText.split("\n").map((line, i) => (
          <p key={i} style={{ marginBottom: 4, fontSize: 12 }}>{line || "\u00A0"}</p>
        ))}
      </div>
    );
  };

  const renderSignatures = () => {
    if (!toggles.show_signature_block) return null;
    return (
      <div style={{ marginTop: 40 }}>
        <h3 style={{ fontSize: 12, textTransform: "uppercase" as const, letterSpacing: 1, borderBottom: "1px solid hsl(var(--border))", paddingBottom: 4, marginBottom: 10 }}>Agreement & Signatures</h3>
        <p style={{ fontSize: 12, marginBottom: 20 }}>By signing below, both parties agree to the scope, terms, and conditions of this Work Order.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
          {toggles.show_company_signature && (
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>AUTHORIZED BY — {(companyProfile?.legal_name ?? companyProfile?.name ?? "COMPANY").toUpperCase()}</p>
              <div style={{ borderTop: "1px solid hsl(var(--foreground))", paddingTop: 4, marginTop: 28, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Signature</div>
              <div style={{ borderTop: "1px solid hsl(var(--foreground))", paddingTop: 4, marginTop: 20, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Name / Title</div>
              <div style={{ borderTop: "1px solid hsl(var(--foreground))", paddingTop: 4, marginTop: 20, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Date</div>
            </div>
          )}
          {toggles.show_sub_signature && (
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>ACCEPTED BY — {(contractor?.name ?? "SUBCONTRACTOR").toUpperCase()}</p>
              <div style={{ borderTop: "1px solid hsl(var(--foreground))", paddingTop: 4, marginTop: 28, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Signature</div>
              <div style={{ borderTop: "1px solid hsl(var(--foreground))", paddingTop: 4, marginTop: 20, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Name / Title</div>
              <div style={{ borderTop: "1px solid hsl(var(--foreground))", paddingTop: 4, marginTop: 20, fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Date</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const sectionRenderers: Record<string, () => React.ReactNode> = {
    scope: renderScope,
    site_info: renderSiteInfo,
    terms: renderTerms,
    contractor_scope: renderContractorScope,
    signatures: renderSignatures,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "transition-all duration-200 flex flex-col p-0",
        expanded ? "max-w-[95vw] h-[95vh]" : "max-w-6xl h-[90vh]"
      )}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 flex-row items-center justify-between">
          <div>
            <DialogTitle className="flex items-center gap-2">
              WO Document — {wo.number}
              <span className="text-sm font-normal text-muted-foreground">
                {contractor?.name} | {lines.length} line{lines.length !== 1 ? "s" : ""}
              </span>
            </DialogTitle>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setExpanded(e => !e)} className="shrink-0">
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Left — Toggles + Section Order */}
          <ScrollArea className="w-[320px] border-r p-4 shrink-0">
            <div className="space-y-5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">What to Include</p>
              {TOGGLE_SECTIONS.map((section) => (
                <div key={section.title} className="space-y-2">
                  <p className="text-xs font-semibold">{section.title}</p>
                  {section.keys.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                      <Checkbox checked={toggles[key]} onCheckedChange={() => toggle(key)} />
                      {label}
                    </label>
                  ))}
                </div>
              ))}

              <Separator />

              {/* Section Order */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Section Order</p>
                <p className="text-xs text-muted-foreground">Drag to reorder document sections</p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1.5">
                      {sectionOrder.map((id) => (
                        <SortableSectionChip key={id} id={id} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>

              <Separator />

              {/* Inline scope editor */}
              {toggles.show_contractor_scope && (
                <div className="space-y-2">
                  <Label className="text-xs">Contractor Scope (editable per-WO)</Label>
                  <Textarea
                    value={scopeText}
                    onChange={(e) => setScopeText(e.target.value)}
                    placeholder="Enter contractor scope of work text…"
                    rows={5}
                    className="text-xs"
                  />
                </div>
              )}

              <Separator />

              <Button variant="outline" size="sm" className="w-full" onClick={handleSaveDefaults} disabled={upsertSettings.isPending}>
                <Save className="h-3.5 w-3.5 mr-1" /> Save as My Defaults
              </Button>
            </div>
          </ScrollArea>

          {/* Right — Preview */}
          <ScrollArea className="flex-1 p-6 bg-muted/30">
            <div ref={previewRef} className="bg-background rounded-lg shadow-sm border p-8 max-w-[700px] mx-auto text-sm leading-relaxed">
              {/* Header */}
              <div className="header" style={{ borderBottom: "3px solid hsl(var(--foreground))", paddingBottom: 12, marginBottom: 20 }}>
                <p style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase" as const, color: "hsl(var(--muted-foreground))" }}>{companyProfile?.legal_name ?? companyProfile?.name ?? "Company"}</p>
                <h2 style={{ fontSize: 20, fontWeight: 700 }}>Work Order Agreement</h2>
              </div>

              {/* Meta */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                <div style={{ background: "hsl(var(--muted))", border: "1px solid hsl(var(--border))", borderRadius: 4, padding: 12 }}>
                  <p style={{ fontSize: 10, textTransform: "uppercase" as const, letterSpacing: 1, color: "hsl(var(--muted-foreground))", marginBottom: 6 }}>Work Order</p>
                  <p><strong>{wo.number}</strong></p>
                  <p>Date: {today}</p>
                  {toggles.show_project_name && <p>Project: {p.name} {p.project_number ? `(${p.project_number})` : ""}</p>}
                  {toggles.show_site_address && p.site_address && <p>{p.site_address}, {p.site_city} {p.site_state} {p.site_zip}</p>}
                </div>
                <div style={{ background: "hsl(var(--muted))", border: "1px solid hsl(var(--border))", borderRadius: 4, padding: 12 }}>
                  <p style={{ fontSize: 10, textTransform: "uppercase" as const, letterSpacing: 1, color: "hsl(var(--muted-foreground))", marginBottom: 6 }}>Subcontractor</p>
                  <p><strong>{contractor?.name ?? "—"}</strong></p>
                  {contractor?.contact_name && <p>Contact: {contractor.contact_name}</p>}
                  {contractor?.contact_phone && <p>Phone: {contractor.contact_phone}</p>}
                </div>
              </div>

              {/* Sections rendered in draggable order */}
              {sectionOrder.map((sectionId) => {
                const renderer = sectionRenderers[sectionId];
                return renderer ? <div key={sectionId}>{renderer()}</div> : null;
              })}

              {/* Footer */}
              <div style={{ marginTop: 40, paddingTop: 12, borderTop: "1px solid hsl(var(--border))" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 4 }}>
                  <img src={commandxLogo} alt="CommandX" style={{ height: 16, objectFit: "contain" }} />
                  <span style={{ fontSize: 10, color: "hsl(var(--muted-foreground))" }}>Powered by CommandX Ops Center</span>
                </div>
                <p style={{ fontSize: 9, color: "hsl(var(--muted-foreground))", textAlign: "center" }}>
                  {companyProfile?.footer_text ?? `${companyProfile?.legal_name ?? companyProfile?.name ?? "Company"} · ${companyProfile?.state ?? ""}`} · {wo.number} · Generated {today}
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* Bottom Actions */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleDownload}>
            <Download className="h-4 w-4 mr-1" /> Download PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
